import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X, Loader2, CheckCircle2, ChevronDown, AlertCircle,
  Lock, Sparkles, ArrowRight, ShieldCheck, Search,
} from "lucide-react";
import { COUNTRIES } from "../../data/countries";
import PROGRAM_MAPPING_FALLBACK from "../../data/programMappingFallback.json";
import { buildAttributionPayload } from "../../hooks/useAttribution";
import { getShortlistSnapshot } from "../../hooks/useShortlist";
import {
  validateEmail, validateName, validatePhone, validatePostal, toTitleCaseName,
} from "../../utils/formValidation";
import { useRequireOtp } from "./OTPModal";
import RouteFallback from "../RouteFallback";
// StepUp's <Inner> only renders inside the embedded "Schedule Interview"
// step after the visitor completes a form + clicks the CTA — pure
// user-gated code path. Lazy-loading it keeps StepUp.jsx (and its
// downstream framer-motion projection/drag modules) out of the main
// bundle even though RequestInfoModal itself is eager-imported by App.js.
// Option (ii) from the perf diagnostic: `React.lazy()` inside an
// eager-imported shell. Option (i) would hoist the shared code back
// into main via RequestInfoModal's static import graph.
const JourneyStepUpInner = lazy(() =>
  import("../../pages/StepUp").then((m) => ({ default: m.Inner }))
);

const API = (typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

import { PHONE_CODES } from "../../utils/phoneCountries";
import Honeypot from "./Honeypot";

// Brand palette — kept local to the modal so reverting is file-scoped
const BRAND_BLUE = "#1F3A68";      // Anderson navy
const BRAND_BLUE_GLOW = "#3B7EE3"; // aurora accent
const GOLD = "#C9A94B";

// ───────────────────── Context ─────────────────────
const RequestInfoContext = createContext({ open: () => {} });
export const useRequestInfo = () => useContext(RequestInfoContext);

export const RequestInfoProvider = ({ children }) => {
  const [isOpen, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({});
  const [mapping, setMapping] = useState(null);
  const [mappingErr, setMappingErr] = useState("");

  useEffect(() => {
    if (mapping) return;
    axios
      .get(`${API}/api/program-mapping`, { timeout: 8000 })
      .then((r) => setMapping(r.data))
      .catch((e) => {
        // Backend can be transiently unavailable (Cloudflare 520 on cold
        // start, mobile carrier proxy issue, etc). Fall back to the baked-in
        // snapshot so the user can still submit a lead — staleness is
        // acceptable, missed leads are not.
        console.warn("program-mapping fetch failed — using baked-in fallback", e);
        setMapping(PROGRAM_MAPPING_FALLBACK);
        setMappingErr("");
      });
  }, [mapping]);

  // Lock body scroll while the modal is open so the page behind doesn't
  // scroll when the user scrolls within the (now-scrollable) overlay.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const open = useCallback((values = {}) => {
    setPrefill(values || {});
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setPrefill({});
  }, []);

  const ctx = useMemo(() => ({ open }), [open]);

  return (
    <RequestInfoContext.Provider value={ctx}>
      {children}
      {isOpen && (
        <RequestInfoModal
          prefill={prefill}
          mode={prefill?.mode || "info"}
          stayOnPage={!!prefill?.stayOnPage}
          onClose={close}
          mapping={mapping}
          mappingErr={mappingErr}
        />
      )}
    </RequestInfoContext.Provider>
  );
};

// ───────────────────── Atoms ─────────────────────
// Injected once — keyframes for the agentic micro-interactions
const AGENTIC_CSS = `
@keyframes ac-scan {
  0%   { transform: translateX(-110%); opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { transform: translateX(110%); opacity: 0; }
}
@keyframes ac-aurora {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50%      { opacity: 0.85; transform: scale(1.05); }
}
@keyframes ac-atomize {
  0%   { transform: scale(1);   opacity: 1; filter: blur(0); }
  60%  { transform: scale(1.04); opacity: 0.35; filter: blur(3px); }
  100% { transform: scale(0.8); opacity: 0; filter: blur(6px); }
}
@keyframes ac-verified-pop {
  0%   { transform: scale(0.4); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes ac-cta-aura {
  0%, 100% { box-shadow: 0 0 0 0 rgba(59,126,227,0.55), 0 12px 32px -8px rgba(31,58,104,0.55); }
  50%      { box-shadow: 0 0 0 10px rgba(59,126,227,0.00), 0 18px 44px -10px rgba(31,58,104,0.70); }
}
.ac-scan-line {
  position: absolute; left: 0; right: 0; top: 50%; height: 1.5px;
  background: linear-gradient(90deg, transparent, ${BRAND_BLUE_GLOW}, transparent);
  animation: ac-scan 0.9s cubic-bezier(0.2, 0.7, 0.3, 1) both;
  pointer-events: none;
}
.ac-aurora {
  position: absolute; inset: -40px; border-radius: 28px; pointer-events: none;
  background: radial-gradient(closest-side, rgba(59,126,227,0.38), rgba(59,126,227,0.0) 70%);
  animation: ac-aurora 3.8s ease-in-out infinite;
  z-index: 0;
}
.ac-verified {
  animation: ac-verified-pop 0.45s cubic-bezier(0.2, 0.9, 0.25, 1.2) both;
}
.ac-atomize { animation: ac-atomize 0.45s ease-in both; }
.ac-cta {
  background: linear-gradient(180deg, #1F3A68 0%, #2A4E82 55%, #3B7EE3 100%);
  animation: ac-cta-aura 2.6s ease-in-out infinite;
}
`;

// ───────────────────── Field shell ─────────────────────
/**
 * AField — field shell that hosts:
 *  - label (with optional Lock icon)
 *  - the input (passed as children)
 *  - inline error
 *  - gold "Verified" badge when `verified`
 *  - blue laser-line scan when `scan` briefly flips true
 *  - aurora glow when `active` (focus)
 */
const AField = ({
  label, hint, error, verified, scan, active, locked, children, testId,
}) => (
  <div className="relative" data-testid={testId}>
    {active && <span className="ac-aurora" aria-hidden="true" />}
    <div className="relative z-[1]">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[12px] font-semibold tracking-[0.12em] uppercase text-slate-600 inline-flex items-center gap-1.5">
          {locked && <Lock className="w-3 h-3 text-[color:var(--ac-blue,#3B7EE3)]" />}
          {label}
        </label>
        {verified && (
          <span
            className="ac-verified inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
            style={{ color: GOLD, background: "rgba(201,169,75,0.12)", border: `1px solid ${GOLD}55` }}
            data-testid={testId ? `${testId}-verified` : undefined}
          >
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        )}
      </div>
      <div className="relative">
        {children}
        {scan && <span className="ac-scan-line" aria-hidden="true" />}
      </div>
      {hint && !error && (
        <p className="mt-1 text-[11px] text-slate-500">{hint}</p>
      )}
      {error && (
        <p className="mt-1 text-[11px] text-rose-600 inline-flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}
    </div>
  </div>
);

const inputCls = (err) =>
  `w-full h-12 px-4 rounded-lg bg-white text-slate-900 placeholder:text-slate-400 text-[14px] ` +
  `border transition-all focus:outline-none ` +
  (err
    ? "border-rose-400 focus:ring-2 focus:ring-rose-200"
    : "border-slate-200 focus:border-[color:var(--ac-blue,#3B7EE3)] focus:ring-2 focus:ring-[color:var(--ac-blue,#3B7EE3)]/25");

const selectCls = (err, disabled) =>
  inputCls(err) +
  " pr-10 appearance-none" +
  (disabled ? " bg-slate-100 text-slate-500 cursor-not-allowed" : "");

// ───────────────────── Fuzzy match helpers ─────────────────────
const STOP_WORDS = new Set(["the", "a", "an", "and", "of", "&", "-", ""]);
const ACRONYMS = {
  psw: "personal support worker",
  mla: "medical laboratory assistant",
  mlt: "medical laboratory technology",
  mlat: "medical laboratory assistant technician",
  ioda: "intra oral dental assistant",
  mla_t: "medical laboratory assistant technician",
  ohs: "occupational health safety",
  moa: "medical office administrator",
  ece: "early childhood education",
  eca: "early childhood assistant",
  cyca: "child youth care addictions",
  him: "health information management",
  it: "information technology",
  dms: "diagnostic medical sonographer",
  cardio: "cardiology",
  esth: "esthetician",
  derm: "esthetician",
};
const _normTokens = (s) => {
  const base = (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter((w) => w && !STOP_WORDS.has(w));
  const expanded = [];
  base.forEach((t) => {
    expanded.push(t);
    if (ACRONYMS[t]) expanded.push(...ACRONYMS[t].split(/\s+/));
  });
  return expanded;
};
const matchesQuery = (name, q) => {
  const query = (q || "").trim().toLowerCase();
  if (!query) return true;
  const lower = name.toLowerCase();
  // Fast path: exact substring
  if (lower.includes(query)) return true;
  // Acronym query expansion (e.g. "psw" → "personal support worker")
  const expanded = ACRONYMS[query];
  if (expanded && lower.includes(expanded)) return true;
  // Token overlap: every query token must appear somewhere in the program tokens
  const qTokens = _normTokens(query);
  if (!qTokens.length) return false;
  const pTokens = new Set(_normTokens(name));
  return qTokens.every((qt) =>
    [...pTokens].some((pt) => pt.startsWith(qt) || pt.includes(qt)),
  );
};

// ───────────────────── ProgramCombobox ─────────────────────
/**
 * Searchable combobox with fuzzy match + keyboard nav + glass dropdown.
 * Props:
 *   value, onChange(name), programs (array), locked (bool), error
 *   onFocus/onBlur (relay for aurora), onSelected() fired when a program is committed
 */
export const ProgramCombobox = ({
  value, onChange, programs, locked = false, error,
  onFocus, onBlur, onSelected,
}) => {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Build delivery badges per program. API returns `deliveries` (flat list).
  const withBadges = useMemo(
    () => programs.map((p) => ({
      ...p,
      modes: Array.from(new Set(p.deliveries || [])).sort(),
    })),
    [programs],
  );

  // If user has typed but the value field still holds a previous selection, treat the query as the filter.
  // When the field is "locked preselected" and user clicks to browse, we clear query to show the full list.
  const filtered = useMemo(() => {
    const q = query === value ? "" : query; // clicking a pre-filled value clears filter (per spec)
    return withBadges.filter((p) => matchesQuery(p.name, q));
  }, [withBadges, query, value]);

  useEffect(() => { setCursor(0); }, [query, open]);

  const commit = (name) => {
    onChange(name);
    setQuery(name);
    setOpen(false);
    onSelected && onSelected(name);
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setCursor((c) => Math.min(c + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      if (open && filtered[cursor]) {
        e.preventDefault();
        commit(filtered[cursor].name);
      }
    } else if (e.key === "Tab" && filtered.length === 1) {
      // Auto-complete when exactly one match remains
      e.preventDefault();
      commit(filtered[0].name);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleFocus = () => {
    // If field is pre-populated, clear the visible query to show full list (per spec)
    if (value && query === value) setQuery("");
    setOpen(true);
    onFocus && onFocus();
  };

  const handleBlur = (e) => {
    // Defer closing so option clicks can register
    setTimeout(() => {
      // If user didn't select but typed nothing, restore previous value
      if (!query && value) setQuery(value);
      onBlur && onBlur();
    }, 120);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="program-combobox">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={locked ? "Pre-selected for you" : "Search programs — e.g. PSW, Dental, Medical…"}
          value={query}
          disabled={locked}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKey}
          className={inputCls(error) + " pr-10"}
          style={locked ? {
            color: BRAND_BLUE, fontWeight: 700,
            background: "rgba(59,126,227,0.06)",
            borderColor: "rgba(59,126,227,0.35)",
            textShadow: "0 0 12px rgba(59,126,227,0.35)",
          } : undefined}
          data-testid="request-info-program"
        />
        <Search
          className={`w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition ${
            open ? "text-[color:var(--ac-blue,#3B7EE3)]" : "text-slate-400"
          }`}
          style={open ? { filter: "drop-shadow(0 0 6px rgba(59,126,227,0.6))" } : undefined}
        />
      </div>

      <AnimatePresence>
        {open && !locked && (
          <motion.div
            initial={{ opacity: 0, y: -6, scaleY: 0.92 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -6, scaleY: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            style={{
              transformOrigin: "top",
              background: "rgba(255,255,255,0.96)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              boxShadow: "0 18px 40px -12px rgba(15,39,80,0.28), 0 0 0 1px rgba(31,58,104,0.08)",
            }}
            className="relative mt-1.5 rounded-xl overflow-hidden max-h-72 overflow-y-auto"
            role="listbox"
            data-testid="program-combobox-list"
          >
            {filtered.length === 0 ? (
              <div className="px-4 py-5 text-[13px] text-slate-500" data-testid="program-combobox-empty">
                <div className="inline-flex items-center gap-1.5 text-slate-700 font-semibold">
                  <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND_BLUE_GLOW }} />
                  We don't see that program yet
                </div>
                <div className="mt-1 text-slate-500">
                  Try "<button type="button" className="underline hover:text-[color:var(--ac-blue,#3B7EE3)]" onClick={() => setQuery("Healthcare")}>Healthcare</button>" or "<button type="button" className="underline hover:text-[color:var(--ac-blue,#3B7EE3)]" onClick={() => setQuery("Dental")}>Dental</button>".
                </div>
              </div>
            ) : (
              <AnimatePresence initial={false}>
                {filtered.map((p, i) => {
                  const active = i === cursor;
                  const sole = filtered.length === 1;
                  return (
                    <motion.button
                      key={p.name}
                      type="button"
                      role="option"
                      aria-selected={active}
                      initial={{ opacity: 0, y: -3 }}
                      animate={{ opacity: 1, y: 0, transition: { duration: 0.14 } }}
                      exit={{
                        opacity: 0,
                        y: -4,
                        filter: "blur(2px)",
                        transition: { duration: 0.22, delay: (filtered.length - i) * 0.04 },
                      }}
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => commit(p.name)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-[13.5px] transition"
                      style={{
                        background: active ? "rgba(59,126,227,0.09)" : "transparent",
                        color: active ? BRAND_BLUE : "#1a1a1a",
                        fontWeight: active || sole ? 600 : 500,
                        borderLeft: sole
                          ? `3px solid ${BRAND_BLUE_GLOW}`
                          : "3px solid transparent",
                      }}
                      data-testid={`program-option-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    >
                      <span className="truncate">{p.name}</span>
                      <span className="flex gap-1 shrink-0">
                        {p.modes.map((m) => (
                          <span
                            key={m}
                            className="text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{
                              color: m === "Online"
                                ? "#047857"
                                : m === "Hybrid" ? BRAND_BLUE : "#7c2d12",
                              background: m === "Online"
                                ? "rgba(5,150,105,0.10)"
                                : m === "Hybrid" ? "rgba(59,126,227,0.10)" : "rgba(124,45,18,0.08)",
                            }}
                          >
                            {m}
                          </span>
                        ))}
                      </span>
                    </motion.button>
                  );
                })}
              </AnimatePresence>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ───────────────────── Modal ─────────────────────
const LAYER_LABELS = ["Identity", "Residency", "Your Path"];

const RequestInfoModal = ({ prefill, mode = "info", stayOnPage = false, onClose, mapping, mappingErr }) => {
  const navigate = useNavigate();
  const { requireOtp } = useRequireOtp();
  const [layer, setLayer] = useState(0); // 0..2
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [verified, setVerified] = useState({});      // { firstName: true, ... }
  const [scanning, setScanning] = useState({});      // { email: true } transient
  const [focused, setFocused] = useState("");
  // Embedded Admissions Journey (stayOnPage mode only) — once the form
  // is submitted, the host page wants the user to complete the entire
  // step-up flow (legal → OTP → Ontario → booking) IN THE SAME MODAL,
  // never on a new page. These two flags govern that handoff.
  const [journeyPayload, setJourneyPayload] = useState(null); // Inner payload
  const [journeyStarted, setJourneyStarted] = useState(false); // user clicked CTA
  const [journeyDone, setJourneyDone] = useState(false);       // Inner finished
  const [bursaryReveal, setBursaryReveal] = useState(false);

  // Best-effort pre-selected program from prefill (exact → contains → token-overlap)
  const initialProgram = useMemo(() => {
    if (!mapping || !prefill.program) return "";
    const raw = prefill.program.trim();
    const target = raw.toLowerCase();
    let hit = mapping.programs.find((p) => p.name.toLowerCase() === target);
    if (hit) return hit.name;
    hit = mapping.programs.find(
      (p) => p.name.toLowerCase().includes(target) || target.includes(p.name.toLowerCase()),
    );
    if (hit) return hit.name;
    const stop = new Set(["the", "a", "an", "and", "of", "&", "-", ""]);
    const norm = (s) =>
      s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").split(/\s+/).filter((w) => w && !stop.has(w));
    const ACRONYMS = {
      psw: "personal support worker", mla: "medical laboratory assistant",
      mlt: "medical laboratory technology", ecа: "early childhood",
      ohs: "occupational health safety", moa: "medical office administrator",
    };
    const targetTokens = new Set(
      norm(raw).flatMap((t) => [t, ACRONYMS[t]].filter(Boolean).flatMap(norm)),
    );
    let bestScore = 0, best = null;
    for (const p of mapping.programs) {
      const tokens = new Set(norm(p.name).flatMap((t) => [t, ACRONYMS[t]].filter(Boolean).flatMap(norm)));
      let score = 0;
      for (const t of targetTokens) if (tokens.has(t)) score += 1;
      if (score > bestScore) { bestScore = score; best = p.name; }
    }
    return bestScore >= 1 ? best : "";
  }, [mapping, prefill.program]);

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", phoneCountryCode: "+1",
    program: "", programDelivery: "", campus: "",
    country: "Canada", postalCode: "", consent: true,
    // Honeypot — invisible field; bots fill it, humans don't (see services/security.py).
    website: "",
  });

  // Pre-fill + lock program when launched from a program page
  const programLocked = !!initialProgram;
  useEffect(() => {
    if (initialProgram) {
      setForm((f) => (f.program ? f : { ...f, program: initialProgram }));
      setVerified((v) => ({ ...v, program: true }));
    }
  }, [initialProgram]);

  // Admissions Concierge fallback — if the modal opens with no explicit
  // program (e.g. from the header GET STARTED button) and the user has
  // previously shortlisted programs, pre-select the first saved one.
  // The full shortlist still flows through at submit as additional_interests.
  useEffect(() => {
    if (initialProgram || !mapping) return;
    const snap = getShortlistSnapshot();
    if (!snap.length) return;
    const first = snap[0];
    const hit = mapping.programs.find((p) => {
      const a = p.name.toLowerCase().replace(/\s+/g, "-");
      const b = (first.slug || first.name || "").toLowerCase().replace(/\s+/g, "-");
      return a === b || a.includes(b) || b.includes(a);
    });
    if (hit) setForm((f) => (f.program ? f : { ...f, program: hit.name }));
  }, [initialProgram, mapping]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setFieldErrors((fe) => {
      const next = { ...fe };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
  };

  // Flash a laser scan line for `key`, then (if passes) drop a Verified badge
  const runScan = (key, passed) => {
    setScanning((s) => ({ ...s, [key]: true }));
    setTimeout(() => {
      setScanning((s) => ({ ...s, [key]: false }));
      setVerified((v) => ({ ...v, [key]: !!passed }));
    }, 900);
  };

  const verifyField = (key, value) => {
    let ok = false;
    if (["firstName", "lastName"].includes(key)) ok = validateName(key, value).ok;
    else if (key === "email") ok = validateEmail(value).ok;
    else if (key === "phone") ok = validatePhone(value, form.phoneCountryCode).ok;
    else if (key === "postalCode") ok = validatePostal(value, form.country).ok;
    else if (key === "country") ok = !!value;
    else if (key === "program") ok = !!value;
    else if (key === "programDelivery") ok = !!value;
    else if (key === "campus") ok = !!value;
    runScan(key, ok);
    if (!ok) {
      // Inline error surfaced on blur so user isn't punished while typing
      const r = key === "email" ? validateEmail(value)
        : key === "phone" ? validatePhone(value, form.phoneCountryCode)
        : key === "postalCode" ? validatePostal(value, form.country)
        : ["firstName", "lastName"].includes(key) ? validateName(key, value)
        : { error: "Required" };
      setFieldErrors((fe) => ({ ...fe, [key]: r.error || "Required" }));
    }
  };

  // Program metadata
  const programMeta = useMemo(
    () => mapping?.programs.find((p) => p.name === form.program) || null,
    [mapping, form.program],
  );

  useEffect(() => {
    if (!programMeta) return;
    if (programMeta.deliveries.length === 1) {
      update({ programDelivery: programMeta.deliveries[0], campus: "" });
      setVerified((v) => ({ ...v, programDelivery: true }));
    } else if (form.programDelivery && !programMeta.deliveries.includes(form.programDelivery)) {
      update({ programDelivery: "", campus: "" });
      setVerified((v) => ({ ...v, programDelivery: false, campus: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programMeta]);

  const campusOptions = useMemo(() => {
    if (!programMeta || !form.programDelivery) return [];
    return programMeta.campusesByDelivery[form.programDelivery] || [];
  }, [programMeta, form.programDelivery]);

  useEffect(() => {
    if (campusOptions.length === 1 && form.campus !== campusOptions[0]) {
      update({ campus: campusOptions[0] });
      setVerified((v) => ({ ...v, campus: true }));
    }
    if (campusOptions.length === 0 && form.campus) {
      update({ campus: "" });
      setVerified((v) => ({ ...v, campus: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusOptions.join("|")]);

  const campusHidden = campusOptions.length <= 1; // online / single-campus flow

  // Qualification meter — % of expected mandatory fields verified (scales with whether campus picker is visible)
  const progress = useMemo(() => {
    const req = ["firstName", "lastName", "email", "phone", "country", "postalCode", "program", "programDelivery"];
    if (!campusHidden) req.push("campus");
    const done = req.filter((k) => verified[k]).length;
    return Math.round((done / req.length) * 100);
  }, [verified, campusHidden]);

  // ───────── Layer validation ─────────
  const validateLayer0 = () => {
    const errs = {};
    for (const [k, label] of [["firstName", "First name"], ["lastName", "Last name"]]) {
      const r = validateName(label, form[k]);
      if (!r.ok) errs[k] = r.error;
    }
    const eR = validateEmail(form.email); if (!eR.ok) errs.email = eR.error;
    const pR = validatePhone(form.phone, form.phoneCountryCode); if (!pR.ok) errs.phone = pR.error;
    setFieldErrors((fe) => ({ ...fe, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const validateLayer1 = () => {
    const errs = {};
    if (!form.country) errs.country = "Choose a country.";
    const r = validatePostal(form.postalCode, form.country);
    if (!r.ok) errs.postalCode = r.error;
    setFieldErrors((fe) => ({ ...fe, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const validateLayer2 = () => {
    const errs = {};
    if (!form.program) errs.program = "Please choose a program.";
    if (!form.programDelivery) errs.programDelivery = "Select delivery mode.";
    if (!campusHidden && !form.campus) errs.campus = "Choose a campus.";
    if (!form.consent) errs.consent = "Consent is required.";
    setFieldErrors((fe) => ({ ...fe, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (layer === 0 && !validateLayer0()) return;
    if (layer === 1 && !validateLayer1()) return;
    // Event registration only needs identity + residency — skip the program
    // cascade entirely and go straight to submit on Layer-1 advance.
    if (mode === "event_registration" && layer === 1) {
      submit();
      return;
    }
    setLayer((l) => Math.min(l + 1, 2));
  };
  const back = () => setLayer((l) => Math.max(l - 1, 0));

  const submit = async () => {
    // Skip program-cascade validation for event registration.
    if (mode !== "event_registration" && !validateLayer2()) return;
    setSubmitting(true); setSubmitErr("");
    try {
      const cleanForm = {
        ...form,
        firstName: toTitleCaseName(form.firstName),
        lastName: toTitleCaseName(form.lastName),
      };

      // Submit-first, verify-after (2026-02-15). Lead reaches CampusLogin
      // the moment the user clicks Submit — no OTP gate blocks the funnel.
      // Trust token is empty on the initial POST so `phone_verified=false`.
      // A background OTP is kicked off after the POST succeeds (below) and
      // on completion PATCHes the enquiry doc via /api/enquiry/{id}/verify-phone.
      // If the user closes the OTP modal, the lead is still in CampusLogin.
      const trustToken = "";
      const _stripped = (form.phone || "").replace(/\D/g, "");
      const _e164 = form.phoneCountryCode
        ? `${form.phoneCountryCode}${_stripped}`
        : `+1${_stripped}`;

      // Helper: kick off OTP in the background as a phone_verified upgrade.
      // Best-effort; failures are swallowed since the lead is already saved.
      const fireBackgroundOtp = (enquiryId) => {
        if (!enquiryId) return;
        requireOtp(_e164, cleanForm.email, form.country)
          .then(async (tk) => {
            if (!tk) return;
            try {
              await axios.post(`${API}/api/enquiry/${enquiryId}/verify-phone`, {
                trust_token: tk,
                phoneCountryCode: form.phoneCountryCode || "+1",
                phone: form.phone,
              });
            } catch (_) { /* best-effort — lead is already in CampusLogin */ }
          })
          .catch(() => { /* user closed OTP modal — lead already saved */ });
      };

      // Event registration takes a shorter path with its own endpoint.
      if (mode === "event_registration") {
        const evRes = await axios.post(`${API}/api/events/register`, {
          event_id: prefill?.eventId || "",
          event_title: prefill?.eventTitle || "",
          firstName: cleanForm.firstName,
          lastName: cleanForm.lastName,
          email: cleanForm.email,
          phone: cleanForm.phone || "",
          phoneCountryCode: cleanForm.phoneCountryCode || "+1",
          trust_token: trustToken,
          website: form.website || "",
        });
        // Background OTP upgrade — event-registration path also benefits from
        // capturing phone_verified for downstream reporting.
        fireBackgroundOtp(evRes.data?.id);
        try {
          sessionStorage.setItem("ac_stepup_payload", JSON.stringify({
            firstName: cleanForm.firstName || "",
            phone: `${form.phoneCountryCode || ""}${form.phone || ""}`.trim(),
            postalCode: form.postalCode || "",
            program: prefill?.eventTitle || "Event Registration",
            source: "event_registration",
          }));
        } catch (_) { /* storage may be blocked */ }
        onClose && onClose();
        navigate("/step-up");
        return;
      }

      const attribution = buildAttributionPayload();
      // Admissions Concierge — if the user had 2+ shortlisted programs,
      // pass every saved program name through as additional_interests so
      // the advisor sees the full decision set on the CampusLogin lead.
      const snap = getShortlistSnapshot();
      const shortlistInterests =
        snap.length > 1
          ? snap.map((s) => s.name).filter(Boolean).join(", ")
          : "";
      // Program Offer path — the OfferCard pre-fills `intake` and the
      // additional_interests gets the intake context appended. The
      // offer's lead_source_tag flows into `source` so CRM analytics
      // can attribute downstream enrolments to the specific intake.
      const offerIntake = prefill?.intake
        ? `Intake: ${prefill.intake}`
        : "";
      const additional_interests = [shortlistInterests, offerIntake]
        .filter(Boolean)
        .join(" · ");
      const offerSource = prefill?.source || null;
      const formType =
        mode === "alumni_bursary" ? "alumni_bursary"
        : mode === "apply" ? "apply"
        : "request_info";
      const res = await axios.post(`${API}/api/enquiry`, {
        ...cleanForm,
        additional_interests,
        attribution,
        formType,
        trust_token: trustToken,
        ...(offerSource ? { source: offerSource } : {}),
      });
      // Fire background OTP upgrade — non-blocking, runs on top of /step-up.
      fireBackgroundOtp(res.data?.id);
      // GTM dataLayer push — lets marketing build conversion tags in GTM
      // without needing a redeploy. PII is stripped inside gtmPush().
      try {
        const { gtmPush, GTMEvents } = await import("../../lib/gtm");
        gtmPush(GTMEvents.LEAD_REQUEST_INFO, {
          program: form.program,
          campus: form.campus,
          country: form.country,
          programDelivery: form.programDelivery,
          postalCode: form.postalCode,
          form_type: formType,
        });
      } catch (_) { /* analytics never breaks the page */ }
      // First-party attribution: log the form_submit event so we can
      // later join CRM leads to paid-click attribution rows.
      try {
        const { trackEvent } = await import("../../utils/attribution");
        trackEvent("form_submit", {
          form_id: "request-info-modal",
          form_name: "RequestInfoModal",
          form_mode: mode,
          program: form.program || null,
          enquiry_id: res.data?.id || null,
        });
      } catch (_) { /* analytics never breaks the page */ }
      // Stash payload for the Step-Up flow and redirect there.
      try {
        sessionStorage.setItem("ac_stepup_payload", JSON.stringify({
          enquiry_id: res.data?.id,
          firstName: cleanForm.firstName || "",
          phone: `${form.phoneCountryCode || ""}${form.phone || ""}`.trim(),
          postalCode: form.postalCode || "",
          program: form.program || "",
          country: form.country || "Canada",
          source: mode === "alumni_bursary" ? "alumni_bursary" : "request_info",
        }));
      } catch (_) { /* sessionStorage may be blocked — fall through */ }

      // Admissions Journey tracker — fire the "form-submitted" event so
      // the Get Started icon turns green. Event payload also includes the
      // country so the tracker can render the correct next-step state
      // (international leads skip the call/book step).
      try {
        window.dispatchEvent(new CustomEvent("ac:journey-action", {
          detail: { action: "form-submitted", country: form.country || "Canada" },
        }));
      } catch (_) { /* CustomEvent unsupported on very old browsers — noop */ }

      // Admissions Tracker integration — when the modal is opened from
      // the Admissions Journey tracker (/admissions/how-to-apply), stay
      // on the same page and show the success panel. The user can then
      // continue with Step 2 (Schedule Interview) on the tracker.
      if (stayOnPage) {
        // Stash the payload for the embedded Inner step-up flow that
        // takes over when the user clicks "Schedule Interview" on the
        // success panel. NO PAGE REDIRECT.
        setJourneyPayload({
          enquiry_id: res.data?.id,
          firstName: cleanForm.firstName || "",
          phone: `${form.phoneCountryCode || ""}${form.phone || ""}`.trim(),
          phoneCountryCode: form.phoneCountryCode || "+1",
          postalCode: form.postalCode || "",
          program: form.program || "",
          country: form.country || "Canada",
          source: "request_info_journey",
        });
        setSuccess(true);
        return;
      }

      // Alumni Bursary reveal — show $1000 unlock animation for non-PSW
      // programs, then proceed to Step-Up. PSW alumni skip the reveal
      // (PSW scholarship already has its own dedicated pathway).
      if (mode === "alumni_bursary") {
        const progLower = (form.program || "").toLowerCase();
        const isPSW = progLower.includes("personal support worker") || /\bpsw\b/.test(progLower);
        if (isPSW) {
          onClose && onClose();
          navigate("/step-up");
        } else {
          setBursaryReveal(true);
          window.setTimeout(() => {
            onClose && onClose();
            navigate("/step-up");
          }, 7500);
        }
        return;
      }

      onClose && onClose();
      navigate("/step-up");
    } catch (e) {
      // Surface the backend's actionable message when present — rate-limits
      // (429), phone verification (403), CRM/validation errors etc. each
      // carry a human-readable `detail` that's far more useful than a flat
      // "Something went wrong." Falls back to the generic copy for opaque
      // network failures.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let message;
      if (status === 429 && typeof detail === "string" && detail) {
        message = detail;
      } else if (status === 403 && detail === "phone_verification_required") {
        message = "Phone verification is required to continue.";
      } else if (typeof detail === "string" && detail && detail.length < 250) {
        message = detail;
      } else {
        message = "Something went wrong. Please try again or call us directly.";
      }
      setSubmitErr(message);
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  // ───────── Render ─────────
  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden"
      data-testid="request-info-modal"
      role="dialog"
      aria-modal="true"
    >
      <style>{AGENTIC_CSS}</style>
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-md"
        onClick={onClose}
        data-testid="request-info-backdrop"
      />
      {/* Single scroll container: scroll happens here only, NOT on the outer
          fixed wrapper (which used to cause a double-scroll on short and
          landscape phones — Phase 8 fix). Anchored top-left with safe top
          padding on phones, centred at tall viewports. */}
      <div
        className="absolute inset-0 overflow-y-auto overscroll-contain flex items-start sm:items-center justify-center pt-24 pb-10 sm:py-10 px-4 md:px-6"
        onClick={onClose}
      >
        {/* Glass container with soft-shadow, no hard border */}
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.25, 1] }}
          className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #F8FAFC 0%, #EEF3FA 100%)",
            boxShadow: "0 40px 80px -20px rgba(15, 39, 80, 0.35), 0 0 0 1px rgba(31,58,104,0.06)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
        <button
          onClick={onClose}
          className="absolute top-3.5 right-4 w-8 h-8 rounded-full bg-white/70 hover:bg-white text-slate-600 hover:text-slate-900 inline-flex items-center justify-center z-10 transition"
          aria-label="Close"
          data-testid="request-info-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 md:px-10 py-8 md:py-10">
          {bursaryReveal && <BursaryUnlockOverlay />}
          {journeyDone ? (
            <JourneyStep2Complete onClose={onClose} />
          ) : journeyStarted && journeyPayload ? (
            <EmbeddedJourneyStepUp
              payload={journeyPayload}
              onComplete={() => setJourneyDone(true)}
            />
          ) : success ? (
            <SuccessPanel
              onClose={onClose}
              mode={mode}
              stayOnPage={stayOnPage}
              onStartJourney={stayOnPage ? () => setJourneyStarted(true) : null}
            />
          ) : mappingErr ? (
            <div className="text-rose-600 text-center py-10">{mappingErr}</div>
          ) : !mapping ? (
            <div className="text-slate-500 text-center py-10">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
              Loading programs…
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4" style={{ color: BRAND_BLUE_GLOW }} />
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.22em]"
                  style={{ color: BRAND_BLUE }}
                  data-testid="request-info-eyebrow"
                >
                  {mode === "apply"
                    ? "Start Your Application"
                    : mode === "alumni_bursary"
                      ? "Alumni Bursary Claim"
                      : mode === "event_registration"
                        ? "Event Registration"
                        : "Intelligent Qualification Hub"}
                </span>
              </div>
              <h2 className="text-[26px] md:text-[30px] font-extrabold leading-tight" style={{ color: BRAND_BLUE }} data-testid="request-info-title">
                {mode === "apply"
                  ? "Begin your Anderson journey."
                  : mode === "alumni_bursary"
                    ? "Welcome back, Anderson alum."
                    : mode === "event_registration"
                      ? `Save your seat${prefill?.eventTitle ? "." : "."}`
                      : "Let's find your path."}
              </h2>
              <p className="mt-1 text-[13px] text-slate-600">
                {LAYER_LABELS[layer]} &nbsp;·&nbsp; Step {layer + 1} of 3
              </p>
              {mode === "apply" && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-md border border-[#1F3A68]/15 bg-[#1F3A68]/5 px-3 py-2"
                  data-testid="request-info-apply-notice"
                >
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-[2px]" style={{ color: BRAND_BLUE }} />
                  <p className="text-[12.5px] text-slate-700 leading-snug">
                    Tell us about yourself and the program you want. <strong>An Admissions Advisor will call you within 1 business day</strong> to walk through eligibility, transcripts, funding options, and next steps.
                  </p>
                </div>
              )}
              {mode === "alumni_bursary" && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-md border border-[#C9A94B]/40 bg-[#C9A94B]/10 px-3 py-2"
                  data-testid="request-info-alumni-notice"
                >
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-[2px]" style={{ color: GOLD }} />
                  <p className="text-[12.5px] text-slate-700 leading-snug">
                    As an Anderson alum, you're eligible for a <strong>$1,000 bursary</strong> toward your next program. Tell us which program you want and we'll apply the credit — no student ID lookup required.
                  </p>
                </div>
              )}
              {mode === "event_registration" && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-md border border-[#1F3A68]/15 bg-[#1F3A68]/5 px-3 py-2"
                  data-testid="request-info-event-notice"
                >
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-[2px]" style={{ color: BRAND_BLUE }} />
                  <p className="text-[12.5px] text-slate-700 leading-snug">
                    Saving your seat for{" "}
                    <strong>{prefill?.eventTitle || "this event"}</strong>. We'll email confirmation details and a calendar invite within minutes.
                  </p>
                </div>
              )}

              {/* Step indicator — 3-dot bar styled to match the chat form
               * + Book a Tour pages so all lead-capture surfaces share one
               * visual language.                                        */}
              <div className="mt-5 mb-7" data-testid="qualification-meter">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10.5px] font-bold tracking-[0.22em] uppercase" style={{ color: "#A21D23" }}>
                    Step {layer + 1} of 3 · {LAYER_LABELS[layer]}
                  </div>
                  <span
                    className="text-[12px] font-bold tabular-nums"
                    style={{ color: BRAND_BLUE }}
                    data-testid="qualification-meter-value"
                  >
                    {progress}% Prepared
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-testid="ri-step-indicator">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className={`h-1 rounded-full transition-all ${
                        i === layer ? "w-9 bg-[#A21D23]"
                        : i < layer ? "w-6 bg-[#A21D23]/55"
                        : "w-6 bg-slate-200"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Layers */}
              <Honeypot
                value={form.website || ""}
                onChange={(v) => update({ website: v })}
              />
              <AnimatePresence mode="wait">
                {layer === 0 && (
                  <motion.div
                    key="layer-identity"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <LayerIdentity
                      form={form} update={update} errors={fieldErrors}
                      verified={verified} scanning={scanning} focused={focused}
                      setFocused={setFocused} verifyField={verifyField}
                    />
                  </motion.div>
                )}
                {layer === 1 && (
                  <motion.div
                    key="layer-residency"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <LayerResidency
                      form={form} update={update} errors={fieldErrors}
                      verified={verified} scanning={scanning} focused={focused}
                      setFocused={setFocused} verifyField={verifyField}
                    />
                  </motion.div>
                )}
                {layer === 2 && (
                  <motion.div
                    key="layer-path"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <LayerPath
                      form={form} update={update} errors={fieldErrors}
                      verified={verified} scanning={scanning} focused={focused}
                      setFocused={setFocused} verifyField={verifyField}
                      programs={mapping.programs}
                      programMeta={programMeta} campusOptions={campusOptions}
                      campusHidden={campusHidden} programLocked={programLocked}
                      intake={prefill?.intake}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {submitErr && (
                <p className="text-rose-600 text-sm text-center mt-4" data-testid="request-info-error">
                  {submitErr}
                </p>
              )}

              {/* CTA row */}
              <div className="flex items-center justify-between mt-8 gap-3">
                <button
                  onClick={layer === 0 ? onClose : back}
                  className="text-[13px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800 transition"
                  data-testid="request-info-back"
                >
                  {layer === 0 ? "Cancel" : "← Back"}
                </button>

                {layer < 2 ? (
                  <button
                    onClick={next}
                    className="ac-cta inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-bold uppercase text-[12px] tracking-[0.18em] transition-all hover:brightness-110"
                    data-testid="request-info-next"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    disabled={submitting}
                    onClick={submit}
                    className="ac-cta inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-bold uppercase text-[12px] tracking-[0.18em] transition-all hover:brightness-110 disabled:opacity-70 disabled:cursor-not-allowed"
                    data-testid="request-info-submit"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Begin Your Path <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
      </div>
    </div>
  );
};

// ───────────────────── Layer 1: Identity ─────────────────────
const LayerIdentity = ({ form, update, errors, verified, scanning, focused, setFocused, verifyField }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
    <AField label="First Name" testId="field-first-name"
      active={focused === "firstName"} verified={verified.firstName} scan={scanning.firstName} error={errors.firstName}
    >
      <input
        type="text" placeholder="Enter first name" value={form.firstName}
        onChange={(e) => update({ firstName: e.target.value })}
        onFocus={() => setFocused("firstName")}
        onBlur={() => {
          const cased = toTitleCaseName(form.firstName);
          if (cased !== form.firstName) update({ firstName: cased });
          setFocused("");
          if (cased) verifyField("firstName", cased);
        }}
        className={inputCls(errors.firstName)}
        data-testid="request-info-first-name"
        autoFocus
      />
    </AField>
    <AField label="Last Name" testId="field-last-name"
      active={focused === "lastName"} verified={verified.lastName} scan={scanning.lastName} error={errors.lastName}
    >
      <input
        type="text" placeholder="Enter last name" value={form.lastName}
        onChange={(e) => update({ lastName: e.target.value })}
        onFocus={() => setFocused("lastName")}
        onBlur={() => {
          const cased = toTitleCaseName(form.lastName);
          if (cased !== form.lastName) update({ lastName: cased });
          setFocused("");
          if (cased) verifyField("lastName", cased);
        }}
        className={inputCls(errors.lastName)}
        data-testid="request-info-last-name"
      />
    </AField>
    <AField label="Email Address" testId="field-email"
      active={focused === "email"} verified={verified.email} scan={scanning.email} error={errors.email}
    >
      <input
        type="email" placeholder="name@example.com" value={form.email}
        onChange={(e) => update({ email: e.target.value })}
        onFocus={() => setFocused("email")}
        onBlur={() => { setFocused(""); if (form.email) verifyField("email", form.email); }}
        className={inputCls(errors.email)}
        data-testid="request-info-email"
      />
    </AField>
    <AField label="Phone Number" testId="field-phone"
      active={focused === "phone"} verified={verified.phone} scan={scanning.phone} error={errors.phone}
    >
      <div className={`flex h-12 bg-white rounded-lg overflow-hidden border transition-all ${
        errors.phone
          ? "border-rose-400"
          : focused === "phone"
            ? "border-[color:var(--ac-blue,#3B7EE3)] ring-2 ring-[color:var(--ac-blue,#3B7EE3)]/25"
            : "border-slate-200"
      }`}>
        <div className="relative h-full">
          <select
            value={form.phoneCountryCode}
            onChange={(e) => update({ phoneCountryCode: e.target.value })}
            className="h-full pl-3 pr-8 bg-transparent text-slate-900 text-[13px] font-semibold appearance-none focus:outline-none"
            data-testid="request-info-phone-code"
          >
            {PHONE_CODES.map((c) => (
              <option key={c.label} value={c.value}>{c.label}</option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <input
          type="tel" inputMode="tel" placeholder="(123) 456-7890" value={form.phone}
          onChange={(e) => update({ phone: e.target.value })}
          onFocus={() => setFocused("phone")}
          onBlur={() => { setFocused(""); if (form.phone) verifyField("phone", form.phone); }}
          className="flex-1 px-3 bg-transparent text-slate-900 placeholder:text-slate-400 text-[14px] focus:outline-none"
          data-testid="request-info-phone"
        />
      </div>
    </AField>
  </div>
);

// ───────────────────── Layer 2: Residency ─────────────────────
const LayerResidency = ({ form, update, errors, verified, scanning, focused, setFocused, verifyField }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
    <AField label="Country of Residence" testId="field-country"
      active={focused === "country"} verified={verified.country} scan={scanning.country} error={errors.country}
    >
      <div className="relative">
        <select
          value={form.country}
          onChange={(e) => { update({ country: e.target.value }); verifyField("country", e.target.value); }}
          onFocus={() => setFocused("country")}
          onBlur={() => setFocused("")}
          className={selectCls(errors.country, false)}
          data-testid="request-info-country"
        >
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </AField>
    <AField label="Postal / ZIP Code" testId="field-postal"
      active={focused === "postalCode"} verified={verified.postalCode} scan={scanning.postalCode} error={errors.postalCode}
      hint={form.country === "Canada" ? "Format: A1A 1A1" : undefined}
    >
      <input
        type="text"
        placeholder={form.country === "Canada" ? "A1A 1A1" : "Enter postal / ZIP"}
        value={form.postalCode}
        onChange={(e) => update({ postalCode: e.target.value.toUpperCase() })}
        onFocus={() => setFocused("postalCode")}
        onBlur={() => { setFocused(""); if (form.postalCode) verifyField("postalCode", form.postalCode); }}
        className={inputCls(errors.postalCode)}
        data-testid="request-info-postal"
      />
    </AField>
  </div>
);

// ───────────────────── Layer 3: Your Path ─────────────────────
const LayerPath = ({
  form, update, errors, verified, scanning, focused, setFocused, verifyField,
  programs, programMeta, campusOptions, campusHidden, programLocked, intake,
}) => {
  // Track campus-atomize transition so the user sees the field "dissolve" when Online is picked
  const [atomizeKey, setAtomizeKey] = useState(0);
  const prevHidden = useRef(campusHidden);
  useEffect(() => {
    if (prevHidden.current === false && campusHidden === true) {
      setAtomizeKey((k) => k + 1);
    }
    prevHidden.current = campusHidden;
  }, [campusHidden]);

  return (
    <div className="space-y-4 md:space-y-5">
      {/* Intake — read-only, only when the modal opened from a Program
          Offer card (prefill.intake set). Shows start date · schedule
          · campus/delivery so the applicant and the CRM lead-row carry
          the exact intake context. */}
      {intake && (
        <div
          data-testid="request-info-intake-readonly"
          className="rounded-lg border border-[#f5c056]/70 bg-[#fff8e8] px-4 py-3"
        >
          <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#8a6400]">
            Intake
          </div>
          <div className="mt-0.5 text-[14.5px] font-semibold text-[#2a2a2a]">
            {intake}
          </div>
        </div>
      )}
      <AField
        label={programLocked ? "Program of Interest (AI-Pre-Selected)" : "Program of Interest"}
        locked={programLocked}
        testId="field-program"
        active={focused === "program"} verified={verified.program} scan={scanning.program} error={errors.program}
        hint={programLocked ? "Pre-optimized from the page you came from." : undefined}
      >
        <ProgramCombobox
          value={form.program}
          programs={programs}
          locked={programLocked}
          error={errors.program}
          onFocus={() => setFocused("program")}
          onBlur={() => setFocused("")}
          onChange={(name) => {
            update({ program: name, programDelivery: "", campus: "" });
          }}
          onSelected={(name) => verifyField("program", name)}
        />
      </AField>

      <div className={`grid grid-cols-1 ${campusHidden ? "" : "md:grid-cols-2"} gap-4 md:gap-5`}>
        <AField label="Delivery Mode" testId="field-delivery"
          active={focused === "programDelivery"} verified={verified.programDelivery}
          scan={scanning.programDelivery} error={errors.programDelivery}
        >
          <div className="relative">
            <select
              value={form.programDelivery}
              disabled={!programMeta}
              onChange={(e) => {
                update({ programDelivery: e.target.value, campus: "" });
                verifyField("programDelivery", e.target.value);
              }}
              onFocus={() => setFocused("programDelivery")}
              onBlur={() => setFocused("")}
              className={selectCls(errors.programDelivery, !programMeta)}
              data-testid="request-info-delivery"
            >
              <option value="">{programMeta ? "Choose delivery…" : "Select a program first"}</option>
              {(programMeta?.deliveries || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </AField>

        {/* Campus field atomizes when the flow becomes online-only */}
        {!campusHidden ? (
          <motion.div
            key="campus-visible"
            initial={{ opacity: 0, scale: 0.98, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <AField label="Campus" testId="field-campus"
              active={focused === "campus"} verified={verified.campus}
              scan={scanning.campus} error={errors.campus}
            >
              <div className="relative">
                <select
                  value={form.campus}
                  onChange={(e) => { update({ campus: e.target.value }); verifyField("campus", e.target.value); }}
                  onFocus={() => setFocused("campus")}
                  onBlur={() => setFocused("")}
                  className={selectCls(errors.campus, false)}
                  data-testid="request-info-campus"
                >
                  <option value="">Choose…</option>
                  {campusOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </AField>
          </motion.div>
        ) : (
          atomizeKey > 0 && (
            <div
              key={`atomize-${atomizeKey}`}
              className="ac-atomize pointer-events-none text-[11px] text-slate-400 self-center justify-self-center"
              aria-hidden="true"
            >
              · · ·
            </div>
          )
        )}
      </div>

      {campusHidden && form.campus && (
        <div
          className="rounded-lg px-4 py-2.5 text-[12px]"
          style={{
            color: BRAND_BLUE,
            background: "rgba(59,126,227,0.08)",
            border: "1px solid rgba(59,126,227,0.25)",
          }}
          data-testid="campus-auto-selected-banner"
        >
          <Sparkles className="inline w-3.5 h-3.5 mr-1.5" />
          {form.programDelivery === "Online"
            ? <>Campus auto-simplified — Student Success team at <strong>{form.campus}</strong> will connect shortly.</>
            : <>Campus auto-simplified for your program choice — <strong>{form.campus}</strong> will connect shortly.</>
          }
        </div>
      )}

      <label className="flex gap-2.5 items-start mt-2 text-[12px] leading-relaxed cursor-pointer text-slate-600">
        <input
          type="checkbox" checked={form.consent}
          onChange={(e) => update({ consent: e.target.checked })}
          className="mt-0.5 w-3.5 h-3.5 accent-[#3B7EE3]"
          data-testid="request-info-consent"
        />
        <span>
          By submitting your information, you consent to receive messages from Anderson College
          at the phone number and email provided. You may unsubscribe at any time.
        </span>
      </label>
      {errors.consent && (
        <p className="text-[11px] text-rose-600 inline-flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {errors.consent}
        </p>
      )}
    </div>
  );
};

// ───────────────────── Alumni Bursary Unlock Overlay ─────────────────────
const BursaryUnlockOverlay = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="absolute inset-0 z-[30] flex items-center justify-center"
    style={{
      background:
        "radial-gradient(circle at 50% 45%, rgba(201,169,75,0.98) 0%, rgba(31,58,104,0.96) 70%)",
    }}
    data-testid="bursary-unlock-overlay"
  >
    <div className="text-center px-6">
      <motion.div
        initial={{ scale: 0.4, rotate: -20, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 18, delay: 0.1 }}
        className="inline-flex w-24 h-24 rounded-full items-center justify-center mb-6"
        style={{
          background: "rgba(255,255,255,0.18)",
          boxShadow: "0 0 0 6px rgba(255,255,255,0.12), 0 20px 50px rgba(0,0,0,0.35)",
        }}
      >
        <Sparkles className="w-12 h-12 text-white" />
      </motion.div>
      <motion.p
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="text-white/80 text-[12px] font-semibold tracking-[0.28em] uppercase mb-2"
        data-testid="bursary-unlock-eyebrow"
      >
        Alumni Reward Unlocked
      </motion.p>
      <motion.h3
        initial={{ y: 18, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-white font-extrabold leading-none mb-4"
        style={{ fontSize: "44px" }}
        data-testid="bursary-unlock-amount"
      >
        $1,000 Alumni Bursary
      </motion.h3>
      <motion.p
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="text-white/90 text-[14px] max-w-sm mx-auto mb-6"
      >
        Applied toward your next program. Redirecting you to complete your profile…
      </motion.p>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 7.2, ease: "easeInOut", delay: 0.2 }}
        className="h-[3px] rounded-full bg-white/70 max-w-[220px] mx-auto origin-left"
      />
    </div>
  </motion.div>
);

// ───────────────────── Success ─────────────────────
// In stayOnPage mode this panel shows the "Step 1 Complete" confirmation
// with a 4-second countdown. While the countdown runs we dispatch an
// `ac:journey-action {action:"redirecting"}` event so the host tracker
// animates the progress bar from 22% → 34%. When the timer hits zero we
// auto-invoke onStartJourney() which swaps in the EmbeddedJourneyStepUp
// (legal status → OTP → Ontario → booking). NO buttons; this is a
// fully automatic handoff so the visitor never loses momentum.
const SuccessPanel = ({ onClose, mode = "info", stayOnPage = false, onStartJourney = null }) => {
  const REDIRECT_SECS = 4;
  const [remaining, setRemaining] = useState(REDIRECT_SECS);
  const [progress, setProgress] = useState(0); // 0..100 fill for ring

  useEffect(() => {
    if (!stayOnPage || typeof onStartJourney !== "function") return undefined;
    // Tell the host tracker to animate the bar from 22% → 34%.
    try {
      window.dispatchEvent(new CustomEvent("ac:journey-action", { detail: { action: "redirecting" } }));
    } catch (_) { /* noop */ }
    // Tick the countdown every ~50ms for a smooth ring fill.
    const start = Date.now();
    const total = REDIRECT_SECS * 1000;
    const iv = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / total) * 100);
      setProgress(pct);
      const secsLeft = Math.max(0, Math.ceil((total - elapsed) / 1000));
      setRemaining(secsLeft);
      if (elapsed >= total) {
        clearInterval(iv);
        onStartJourney();
      }
    }, 50);
    return () => clearInterval(iv);
  }, [stayOnPage, onStartJourney]);

  if (stayOnPage) {
    // Countdown ring math — SVG circle stroke-dasharray reveal.
    const R = 36;
    const C = 2 * Math.PI * R;
    const offset = C - (progress / 100) * C;
    return (
      <div className="text-center py-6" data-testid="request-info-success">
        <div className="relative inline-flex items-center justify-center mb-5">
          {/* Countdown ring */}
          <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
            <circle cx="48" cy="48" r={R} fill="none" stroke="rgba(201,169,75,0.18)" strokeWidth="6" />
            <circle
              cx="48" cy="48" r={R}
              fill="none"
              stroke={GOLD}
              strokeWidth="6"
              strokeDasharray={C}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 80ms linear" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10" style={{ color: GOLD }} />
          </div>
        </div>
        <h3 className="text-[26px] font-extrabold mb-2" style={{ color: BRAND_BLUE }}>
          Step 1 Complete
        </h3>
        <p className="text-slate-600 text-[14px] max-w-md mx-auto mb-3">
          Your information was received. We&apos;re redirecting you to build your
          profile and schedule your interview.
        </p>
        <p
          className="text-[12px] font-bold uppercase tracking-[0.16em]"
          style={{ color: BRAND_BLUE }}
          data-testid="success-countdown"
          aria-live="polite"
        >
          Continuing in {remaining} {remaining === 1 ? "second" : "seconds"}…
        </p>
      </div>
    );
  }

  return (
    <div className="text-center py-6" data-testid="request-info-success">
      <div
        className="w-16 h-16 rounded-full inline-flex items-center justify-center mb-5"
        style={{ background: "rgba(201,169,75,0.15)", border: `1px solid ${GOLD}55` }}
      >
        <CheckCircle2 className="w-10 h-10" style={{ color: GOLD }} />
      </div>
      <h3 className="text-[26px] font-extrabold mb-2" style={{ color: BRAND_BLUE }}>
        {mode === "apply" ? "Application Started" : "Qualification Complete"}
      </h3>
      <p className="text-slate-600 text-[14px] max-w-md mx-auto mb-7">
        {mode === "apply"
          ? "Your application request has been received. An Admissions Advisor will call you within 1 business day to walk through transcripts, funding, and next steps."
          : "Your profile has been received. An Admissions Advisor will reach out shortly with a custom program package."}
      </p>
      <button
        onClick={onClose}
        className="ac-cta inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-bold uppercase text-[12px] tracking-[0.18em]"
        data-testid="request-info-success-close"
      >
        Close
      </button>
    </div>
  );
};

// ───────────────────── Embedded Admissions Journey ─────────────────────
// Renders the StepUp `Inner` machine *inside* the modal so the visitor
// completes the entire journey (legal → OTP → Ontario → booking) without
// leaving /admissions/how-to-apply. Fires onComplete() when booking (or
// the international-only confirmation) lands.
const EmbeddedJourneyStepUp = ({ payload, onComplete }) => (
  <div data-testid="journey-embedded-stepup" className="-mx-2 sm:-mx-4 md:-mx-6">
    <Suspense fallback={<RouteFallback />}>
      <JourneyStepUpInner
        payload={payload}
        embedded
        onJourneyComplete={onComplete}
      />
    </Suspense>
  </div>
);

// ───────────────────── Journey Step 2 Complete ─────────────────────
// Inline "Step 2 Complete" banner shown after the embedded step-up
// flow finishes. Mirrors the visual language of the Step 1 success
// panel above but with the brand red accent for finality.
const JourneyStep2Complete = ({ onClose }) => (
  <div className="text-center py-6" data-testid="journey-step2-complete">
    <div
      className="w-16 h-16 rounded-full inline-flex items-center justify-center mb-5"
      style={{ background: "rgba(44,138,82,0.15)", border: "1px solid rgba(44,138,82,0.45)" }}
    >
      <CheckCircle2 className="w-10 h-10 text-[#2c8a52]" />
    </div>
    <h3 className="text-[26px] font-extrabold mb-2" style={{ color: BRAND_BLUE }}>
      Step 2 Complete
    </h3>
    <p className="text-slate-600 text-[14px] max-w-md mx-auto mb-7">
      Great work! Your appointment is locked in and your progress tracker has
      advanced. Your Admissions Advisor will call you at the time you picked —
      be ready with any questions you have.
    </p>
    <button
      onClick={onClose}
      className="ac-cta inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-bold uppercase text-[12px] tracking-[0.18em]"
      data-testid="journey-step2-close"
    >
      Continue on Tracker
    </button>
  </div>
);

export default RequestInfoModal;
